// app/components/Equipment.tsx
"use client";

import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";

const GREEN = "rgb(34 150 83)";

const EQUIPMENT = [
  {
    id: 1,
    name: "Dental Scanner",
    description:
      "Advanced 3D intraoral scanning technology for precise digital impressions. Ensures accurate treatment planning and comfortable patient experience.",
    image:
      "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=600&q=80",
  },
  {
    id: 2,
    name: "Digital X-Ray",
    description:
      "High-resolution digital radiography with reduced radiation exposure. Provides instant, crystal-clear diagnostic images for better treatment outcomes.",
    image:
      "https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=600&q=80",
  },
  {
    id: 3,
    name: "Dental Loupes",
    description:
      "Precision magnification loupes for enhanced visibility during procedures. Ergonomic design with LED illumination for optimal surgical precision.",
    image:
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=600&q=80",
  },
  {
    id: 4,
    name: "Laser Technology",
    description:
      "State-of-the-art dental laser for minimally invasive treatments. Offers pain-free procedures with faster healing and precise control.",
    image:
      "https://images.unsplash.com/photo-1606811971618-4486d14f3f99?w=600&q=80",
  },
];

export default function Equipment() {
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { once: true, margin: "-10%" });
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  return (
    <section
      id="equipments"
      ref={rootRef}
      className="relative overflow-hidden bg-gradient-to-b from-gray-900 via-emerald-950 to-gray-900 py-16 sm:py-20 lg:py-24"
    >
      {/* ===== BACKGROUND EFFECTS (HIDE SOME ON MOBILE) ===== */}
      <div className="pointer-events-none absolute inset-0 opacity-10 hidden sm:block">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#059669_1px,transparent_1px),linear-gradient(to_bottom,#059669_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      <div className="pointer-events-none absolute inset-0 hidden sm:block">
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute size-1 rounded-full bg-emerald-400/30"
            style={{
              left: `${(i * 137) % 100}%`,
              top: `${(i * 97) % 100}%`,
            }}
            animate={{ y: [0, -40, 0], opacity: [0.3, 0.7, 0.3] }}
            transition={{
              duration: 4 + (i % 4),
              repeat: Infinity,
              delay: (i % 8) * 0.3,
            }}
          />
        ))}
      </div>

      <motion.div
        className="pointer-events-none absolute right-0 top-20 size-96 rounded-full bg-emerald-500/20 blur-3xl hidden lg:block"
        animate={{ scale: [1, 1.3, 1], x: [0, -60, 0] }}
        transition={{ repeat: Infinity, duration: 15, ease: "easeInOut" }}
      />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        {/* ===== HEADER (UNCHANGED) ===== */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12 sm:mb-16 text-center"
        >
          <motion.span
            className="inline-block rounded-full border-2 border-emerald-400/50 bg-emerald-500/20 px-6 py-2 text-sm font-bold text-emerald-300 backdrop-blur"
            whileHover={{ scale: 1.08 }}
          >
            🔬 Advanced Technology
          </motion.span>

          <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-white">
            Equipped with{" "}
            <span className="bg-gradient-to-r from-emerald-400 to-cyan-300 bg-clip-text text-transparent">
              modern tools
            </span>
          </h2>

          <p className="mx-auto mt-4 sm:mt-6 max-w-2xl text-base sm:text-lg lg:text-xl text-gray-400">
            State-of-the-art technology ensuring safe, precise, and pain-free
            treatments
          </p>
        </motion.div>

        {/* ================= MOBILE SWIPER ================= */}
        <div className="block lg:hidden">
          <Swiper
            modules={[Pagination]}
            pagination={{ clickable: true }}
            spaceBetween={16}
            slidesPerView={1}
            className="pb-10"
          >
            {EQUIPMENT.map((item) => (
              <SwiperSlide key={item.id}>
                <div className="relative overflow-hidden rounded-3xl border-2 border-emerald-500/30 bg-gradient-to-br from-gray-800/80 to-gray-900/80 p-6 backdrop-blur">
                  <div className="relative mb-6 h-48 overflow-hidden rounded-2xl">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  </div>

                  <h3 className="text-xl font-bold text-white">
                    {item.name}
                  </h3>

                  <p className="mt-3 text-sm leading-relaxed text-gray-400">
                    {item.description}
                  </p>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* ================= DESKTOP GRID (UNCHANGED) ================= */}
        <div className="hidden lg:grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          {EQUIPMENT.map((item, index) => {
            const isHovered = hoveredId === item.id;

            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                onMouseEnter={() => setHoveredId(item.id)}
                onMouseLeave={() => setHoveredId(null)}
                className="group relative"
              >
                <motion.div
                  className="relative h-full overflow-hidden rounded-3xl border-2 bg-gradient-to-br from-gray-800/80 to-gray-900/80 p-6 backdrop-blur"
                  whileHover={{ y: -10 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    borderColor: isHovered
                      ? GREEN
                      : "rgba(34, 150, 83, 0.3)",
                    boxShadow: isHovered
                      ? `0 20px 60px ${GREEN}40, 0 0 40px ${GREEN}30`
                      : "0 10px 40px rgba(0,0,0,0.6)",
                  }}
                >
                  <div className="relative mb-6 h-48 overflow-hidden rounded-2xl">
                    <motion.img
                      src={item.image}
                      alt={item.name}
                      className="h-full w-full object-cover"
                      animate={isHovered ? { scale: 1.1 } : { scale: 1 }}
                      transition={{ duration: 0.4 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  </div>

                  <h3 className="text-2xl font-bold text-white">
                    {item.name}
                  </h3>

                  <p className="mt-3 text-sm leading-relaxed text-gray-400">
                    {item.description}
                  </p>

                  {isHovered && (
                    <motion.div
                      className="pointer-events-none absolute inset-0 rounded-3xl"
                      animate={{
                        boxShadow: [
                          `inset 0 0 20px ${GREEN}20`,
                          `inset 0 0 40px ${GREEN}40`,
                          `inset 0 0 20px ${GREEN}20`,
                        ],
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  )}

                  <motion.div
                    className="absolute left-3 top-3 h-8 w-8 border-l-2 border-t-2 border-emerald-500"
                    animate={isHovered ? { opacity: 1 } : { opacity: 0 }}
                  />
                  <motion.div
                    className="absolute bottom-3 right-3 h-8 w-8 border-b-2 border-r-2 border-emerald-500"
                    animate={isHovered ? { opacity: 1 } : { opacity: 0 }}
                  />
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
